import { useState, type FormEvent } from "react";
import { MessageCircleHeart, Send } from "lucide-react";
import { api } from "../lib/api";

export function FeedbackForm({ reportId }: { reportId: string }) {
  const [rating, setRating] = useState(5);
  const [testimonial, setTestimonial] = useState("");
  const [permission, setPermission] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      await api.submitFeedback({
        reportId,
        rating,
        testimonial,
        permissionToUsePublicly: permission
      });
      setSent(true);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not save your feedback.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="my-14 rounded-[2rem] border border-moss/10 bg-white p-7 print:hidden md:p-10">
      <MessageCircleHeart className="text-coral" />
      <h2 className="mt-4 font-display text-3xl">How was your CareerCompass experience?</h2>
      <p className="mt-2 max-w-2xl text-sm leading-6 text-ink/55">Your feedback helps us improve the assessment, recommendations, and report experience.</p>
      {sent ? (
        <p className="mt-5 rounded-2xl bg-lime/30 p-4 font-semibold text-moss">Thank you—your feedback has been saved.</p>
      ) : (
        <form className="mt-6 grid gap-4" onSubmit={submit}>
          <fieldset>
            <legend className="field-label">Overall rating</legend>
            <div className="flex flex-wrap gap-2">
              {[1, 2, 3, 4, 5].map((score) => (
                <button
                  aria-pressed={rating === score}
                  className={`h-11 w-11 rounded-full text-sm font-extrabold transition ${rating === score ? "bg-moss text-lime" : "bg-cream text-moss hover:bg-lime/40"}`}
                  key={score}
                  onClick={() => setRating(score)}
                  type="button"
                >
                  {score}
                </button>
              ))}
            </div>
          </fieldset>
          <label>
            <span className="field-label">Comments (optional)</span>
            <textarea className="field min-h-28" maxLength={1000} placeholder="What was useful, confusing, or missing?" value={testimonial} onChange={(event) => setTestimonial(event.target.value)} />
          </label>
          <label className="flex items-start gap-3 text-sm leading-6 text-ink/60">
            <input className="mt-1 h-4 w-4 accent-moss" type="checkbox" checked={permission} onChange={(event) => setPermission(event.target.checked)} />
            CareerCompass AI may use this comment as a public testimonial. Leave unchecked to keep it private.
          </label>
          {error && <p className="rounded-2xl bg-coral/15 p-4 text-sm font-semibold text-ink">{error}</p>}
          <button className="btn-dark w-fit" disabled={submitting} type="submit"><Send size={16} /> {submitting ? "Saving…" : "Send feedback"}</button>
        </form>
      )}
    </section>
  );
}
